import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

// Libs
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNodeUpdate } from '~/hooks/useNodeUpdate';
import { useNodeId } from '@xyflow/react';
import { useToast } from '~/contexts/ToastContext';

// Utilitários
import { NodeHeaderCustomDeleteAction } from '~/utils/NodeHeaderCustomDeleteAction';
import { NodeHeaderCustomAlertAction } from '~/utils/NodeHeaderCustomAlertAction';
import { CustomInputTexto } from '~/utils/CustomInputTexto';
import { CustomInputVariavel } from '~/utils/CustomInputVariavel';
import { CustomInputDrop } from '~/utils/CustomInputDrop';
import { CustomHandle } from '~/utils/CustomHandle';

// Node de repetição/loop
// Este componente permite repetir a execução de nós em um loop controlado
export const RepetirNode = ({ data }) => {
    const id = useNodeId();
    const { onNodesChange } = useNodeUpdate();
    const variaveis = useSelector((state) => state.variaveis);
    const { showError } = useToast();
    
    const [tipoRepeticao, setTipoRepeticao] = useState(data.tipoRepeticao || "quantidade");
    const [quantidade, setQuantidade] = useState(data.quantidade || "3");
    const [condicao, setCondicao] = useState(data.condicao || "");
    const [dropDataCondicao, setDropDataCondicao] = useState(data.dropDataCondicao || { id: 'condicao', type: null, data: {} });
    const [variavel, setVariavel] = useState(data.variavel || "");
    const [incremento, setIncremento] = useState(data.incremento || "1");
    const [valorInicial, setValorInicial] = useState(data.valorInicial || "1");
    const [valorFinal, setValorFinal] = useState(data.valorFinal || "3");
    
    // Atualiza os estados com os valores do data para o undo/redo
    useEffect(() => { setTipoRepeticao(data.tipoRepeticao || "quantidade"); }, [data.tipoRepeticao]);
    useEffect(() => { setQuantidade(data.quantidade || "3"); }, [data.quantidade]);
    useEffect(() => { setCondicao(data.condicao || ""); }, [data.condicao]);
    useEffect(() => { setDropDataCondicao(data.dropDataCondicao || { id: 'condicao', type: null, data: {} }); }, [data.dropDataCondicao]);
    useEffect(() => { setVariavel(data.variavel || ""); }, [data.variavel]);
    useEffect(() => { setIncremento(data.incremento || "1"); }, [data.incremento]);
    useEffect(() => { setValorInicial(data.valorInicial || "1"); }, [data.valorInicial]);
    useEffect(() => { setValorFinal(data.valorFinal || "3"); }, [data.valorFinal]);
    
    const [listaPendencias, setListaPendencias] = useState([]);
    
    const conferePendencias = () => {
        const pendencias = [];
        
        console.log('🔍 RepetirNode - conferePendencias:', {
            tipoRepeticao,
            quantidade,
            condicao,
            dropDataCondicao,
            variavel,
            valorInicial,
            valorFinal,
            dataFromNode: data
        });
        
        if (!tipoRepeticao) {
            pendencias.push("Tipo de repetição não definido");
        }
        
        if (tipoRepeticao === "quantidade" && !quantidade) {
            pendencias.push("Quantidade de repetições não definida");
        }
        
        if (tipoRepeticao === "condicao" && !dropDataCondicao.type && !dropDataCondicao.data.value) {
            pendencias.push("Condição de repetição não definida");
        }
        
        if (tipoRepeticao === "para" && !variavel) {
            pendencias.push("Variável deve ser definida para loop 'para'");
        }
        
        console.log('🔍 RepetirNode - pendencias encontradas:', pendencias);
        
        setListaPendencias(pendencias);
        return pendencias;
    };

    const handleTipoChange = (novoTipo) => {
        console.log('🔄 Mudando tipo para:', novoTipo);
        setTipoRepeticao(novoTipo);
        const novoData = { ...data, tipoRepeticao: novoTipo };
        console.log('📊 Novo data após tipo:', novoData);
        onNodesChange([{ id, type: 'update', data: novoData }]);
    };

    const handleQuantidadeChange = (v) => {
        console.log('🔢 Mudando quantidade para:', v);
        setQuantidade(v);
        const novoData = { ...data, quantidade: v };
        console.log('📊 Novo data após quantidade:', novoData);
        onNodesChange([{ id, type: 'update', data: novoData }]);
    };

    // Removido handleCondicaoChange pois não está sendo usado
    // A condição é gerenciada pelo dropDataCondicao

    const handleDropCondicaoChange = (newDrop) => {
        console.log('🎯 Mudando dropDataCondicao para:', newDrop);
        setDropDataCondicao(newDrop);
        const novoData = { ...data, dropDataCondicao: newDrop };
        console.log('📊 Novo data após dropDataCondicao:', novoData);
        onNodesChange([{ id, type: 'update', data: novoData }]);
    };

    const handleVariavelChange = (v) => {
        console.log('🔤 Mudando variável para:', v);
        
        // Verifica se a variável selecionada tem valor numérico válido
        if (v && tipoRepeticao === 'para') {
            const variavelLimpa = v.replace(/[{}]/g, '');
            const variavelExistente = variaveis.find(variavel => variavel.nome === variavelLimpa);
            
            if (variavelExistente) {
                const valorNumerico = parseFloat(variavelExistente.valor);
                if (isNaN(valorNumerico)) {
                    showError(`🚫 Variável "${variavelLimpa}" tem valor "${variavelExistente.valor}" que não é numérico válido. Campo limpo para evitar erros.`, 5000);
                    setVariavel('');
                    const novoData = { ...data, variavel: '' };
                    console.log('📊 Limpando variável devido a valor não numérico');
                    onNodesChange([{ id, type: 'update', data: novoData }]);
                    return;
                }
            }
        }
        
        setVariavel(v);
        const novoData = { ...data, variavel: v };
        console.log('📊 Novo data após variável:', novoData);
        onNodesChange([{ id, type: 'update', data: novoData }]);
    };

    const handleIncrementoChange = (v) => {
        console.log('➕ Mudando incremento para:', v);
        setIncremento(v);
        const novoData = { ...data, incremento: v };
        console.log('📊 Novo data após incremento:', novoData);
        onNodesChange([{ id, type: 'update', data: novoData }]);
    };

    const handleValorFinalChange = (v) => {
        console.log('🏁 Mudando valor final para:', v);
        setValorFinal(v);
        const novoData = { ...data, valorFinal: v };
        console.log('📊 Novo data após valor final:', novoData);
        onNodesChange([{ id, type: 'update', data: novoData }]);
    };

    useEffect(() => { conferePendencias(); }, [tipoRepeticao, quantidade, condicao, dropDataCondicao, variavel, valorInicial, valorFinal]);

    return (
        <div className={`node node-repetir ${data.highlighted ? 'highlighted' : ''}`}>
            <div className="titulo-node">
                <FontAwesomeIcon icon="fa-solid fa-repeat" />
                REPETIR
            </div>
            <div className="linha-separadora" />
            <div className="node-geral">
                <div className="node-botoes">
                    <button title="Executar este comando" onClick={() => alert("Executar")}>
                        <FontAwesomeIcon icon="fa-solid fa-circle-play" />
                    </button>
                    <NodeHeaderCustomDeleteAction />
                    {listaPendencias.length > 0 && (
                        <NodeHeaderCustomAlertAction 
                            conferePendencias={conferePendencias} 
                            nodeData={data} 
                        />
                    )}
                </div>
                <div className="node-principal">
                    <div className="body-node"></div>
                    
                    {/* Tipo de repetição */}
                    <div style={{ marginBottom: '12px' }}>
                        <span className="icon">Tipo:</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input 
                                    type="radio" 
                                    name={`tipo-${id}`}
                                    value="quantidade" 
                                    checked={tipoRepeticao === 'quantidade'} 
                                    onChange={(e) => handleTipoChange(e.target.value)}
                                />
                                <span style={{ fontSize: '13px' }}>Repetir X vezes</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input 
                                    type="radio" 
                                    name={`tipo-${id}`}
                                    value="condicao" 
                                    checked={tipoRepeticao === 'condicao'} 
                                    onChange={(e) => handleTipoChange(e.target.value)}
                                />
                                <span style={{ fontSize: '13px' }}>Enquanto condição</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input 
                                    type="radio" 
                                    name={`tipo-${id}`}
                                    value="para" 
                                    checked={tipoRepeticao === 'para'} 
                                    onChange={(e) => handleTipoChange(e.target.value)}
                                />
                                <span style={{ fontSize: '13px' }}>Para (com contador)</span>
                            </label>
                        </div>
                    </div>

                    {/* Configurações específicas por tipo */}
                    {tipoRepeticao === 'quantidade' && (
                        <div style={{ marginBottom: '8px' }}>
                            <span className="icon">Quantidade:</span>
                            <CustomInputTexto
                                value={quantidade}
                                onChange={handleQuantidadeChange}
                                placeholder="Ex: 5"
                                style={{ width: '80px' }}
                            />
                        </div>
                    )}

                    {tipoRepeticao === 'condicao' && (
                        <div style={{ marginBottom: '8px' }}>
                            <span className="icon">Condição:</span>
                            <CustomInputDrop
                                dropData={dropDataCondicao}
                                dropId="condicao"
                                onDropChange={handleDropCondicaoChange}
                            />
                        </div>
                    )}

                    {tipoRepeticao === 'para' && (
                        <>
                            <div style={{ marginBottom: '8px' }}>
                                <span className="icon">Variável:</span>
                                <CustomInputVariavel
                                    value={variavel}
                                    onChange={handleVariavelChange}
                                    variaveis={variaveis}
                                    placeholder="Ex: {{i}}"
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <div style={{ flex: 1 }}>
                                    <span className="icon" style={{ fontSize: '12px' }}>Até:</span>
                                    <CustomInputTexto
                                        value={valorFinal}
                                        onChange={handleValorFinalChange}
                                        placeholder="3"
                                        style={{ width: '80px' }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <span className="icon" style={{ fontSize: '12px' }}>Passo:</span>
                                    <CustomInputTexto
                                        value={incremento}
                                        onChange={handleIncrementoChange}
                                        placeholder="1"
                                        style={{ width: '80px' }}
                                    />
                                </div>
                            </div>
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                💡 O valor inicial será o valor atual da variável
                            </div>
                        </>
                    )}

                    {/* Indicador visual para o loop */}
                    <div style={{
                        position: 'absolute',
                        right: '5px',
                        top: '47%',
                        fontSize: '10px',
                        color: '#ff9800',
                        fontWeight: 'bold',
                        pointerEvents: 'none'
                    }}>
                        REPETIR
                    </div>

                    {/* Handle de entrada */}
                    <CustomHandle 
                        type="target" 
                        position="top" 
                        connectionCount={1} 
                        id="input"
                    />
                    
                    {/* Handle de saída para próximo nó */}
                    <CustomHandle 
                        type="source" 
                        position="bottom" 
                        connectionCount={1} 
                        id="next"
                    />
                    
                    {/* Handle de saída para loop - com estilo visual distinto */}
                    <CustomHandle 
                        type="source" 
                        position="right" 
                        connectionCount={1} 
                        id="loop"
                        style={{ 
                            backgroundColor: '#ff9800',
                            border: '2px solid #f57c00',
                            top: '50%',
                            right: '-8px',
                            width: '12px',
                            height: '12px'
                        }} 
                    />
                    
                </div>
            </div>
        </div>
    );
};
